import {DetailsFieldsCard, DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {Trans, useTranslation} from 'react-i18next'
import {HttpGetPlannedAccessesDetailResponse} from '@/features/planned-accesses/http/plannedAccesses.http'
import {calculateTotalAccesses, frequencyAccessesToLabel} from '../../utils'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {ProjectExtended} from '../../types'
import {useOutletContext} from 'react-router-dom'
import {ProjectPlannedAccessesEditButton} from '@/features/projects/components/project-planned-accesses-edit-button/ProjectPlannedAccessesEditButton'
import {usePlannedAccessesCalculations} from '@/features/projects/hooks/usePlannedAccessesCalculations'
import {PrintFm22Dialog} from '@/features/projects/components/print-fm-22-dialog/PrintFm22Dialog'
import {useMemo} from 'react'
import {Divider} from '@/components/ui/divider/Divider'

export const PlannedAccessCard = ({plannedAccesses}: {plannedAccesses: HttpGetPlannedAccessesDetailResponse}) => {
    const {t} = useTranslation()
    const {project, isEditable} = useOutletContext<{project: ProjectExtended; isEditable: boolean}>()

    const {
        totalPlannedAccesses,
        totalPlannedAccessesInAbsence,
        totalPlannedAccessesInPresence,
        totalPlannedTreatments,
        totalSingleAccesses,
        totalUnaccountedPlannedAccesses
    } = usePlannedAccessesCalculations(project.durationDays ?? 0, plannedAccesses)

    const totalPlannedAccessesInAbsencePercentage = useMemo(() => {
        return project.durationDays
            ? Number(((totalPlannedAccessesInAbsence / totalPlannedAccessesInPresence) * 100).toFixed(2))
            : 0
    }, [totalPlannedAccessesInAbsence, totalPlannedAccessesInPresence, project.durationDays])

    return (
        <DetailsFieldsSection
            variant="neutral"
            infoTitleSlot={
                <Text size="textLg">
                    <Trans i18nKey="projects:x_total_accesses" values={{value: totalPlannedAccesses}} />
                </Text>
            }
            title={t('projects:planned_accesses')}
            width="100%"
            height="100%"
            headingEndSlot={
                <Flexbox gap={1}>
                    {isEditable && <ProjectPlannedAccessesEditButton project={project} />}
                    {project.status === 'open' && <PrintFm22Dialog project={project} />}
                </Flexbox>
            }
        >
            <Flexbox direction="column" gap={4} fullWidth>
                <Text palette="neutral" shade="950" weight="500" size="textSm">
                    <Trans i18nKey="projects:patient_presence_count" values={{value: totalPlannedAccessesInPresence}} />
                </Text>
                <Flexbox direction="column" gap={1} fullWidth>
                    <Grid columns={3} fullWidth>
                        <GridItem spanColumn={2}>
                            <Text palette="neutral" shade="950" weight="500">
                                {t('projects:treatments')}
                            </Text>
                        </GridItem>
                        <GridItem>
                            <Text palette="neutral" shade="950" weight="500" size="textSm">
                                {totalPlannedTreatments}
                            </Text>
                        </GridItem>
                    </Grid>
                    <DetailsFieldsCard size="sm" fullWidth gap={2} direction="column">
                        {plannedAccesses.treatments.map((treatment, index) => (
                            <Grid columns={3} gap={1} fullWidth key={index}>
                                <Text palette="neutral" shade="950" weight="500" size="textSm">
                                    {treatment.accessName}
                                </Text>

                                <Text palette="neutral" shade="950" weight="500" size="textSm">
                                    {treatment.frequency &&
                                        t(frequencyAccessesToLabel[treatment.frequency], {
                                            count: treatment.numberOfAccesses
                                        })}
                                </Text>

                                <Text palette="neutral" shade="950" weight="500" size="textSm">
                                    {treatment.numberOfAccesses
                                        ? calculateTotalAccesses(
                                              project.durationDays ?? 0,
                                              treatment.frequency ?? 'weekly',
                                              treatment.numberOfAccesses
                                          )
                                        : '-'}
                                </Text>
                            </Grid>
                        ))}
                    </DetailsFieldsCard>
                </Flexbox>

                <Flexbox direction="column" gap={1} fullWidth>
                    <Grid columns={3} fullWidth>
                        <GridItem spanColumn={2}>
                            <Text palette="neutral" shade="950" weight="500">
                                {t('commons:single_accesses')}
                            </Text>
                        </GridItem>
                        <GridItem>
                            <Text palette="neutral" shade="950" weight="500" size="textSm">
                                {totalSingleAccesses}
                            </Text>
                        </GridItem>
                    </Grid>
                    <DetailsFieldsCard size="sm" fullWidth gap={2} direction="column">
                        {plannedAccesses.in_person_accesses.map((access, index) => (
                            <Grid columns={3} gap={1} fullWidth key={index}>
                                <GridItem spanColumn={2}>
                                    <Text palette="neutral" shade="950" weight="500" size="textSm">
                                        {access.accessName}
                                    </Text>
                                </GridItem>
                                <GridItem>
                                    <Text palette="neutral" shade="950" weight="500" size="textSm">
                                        {access.numberOfAccesses}
                                    </Text>
                                </GridItem>
                            </Grid>
                        ))}
                    </DetailsFieldsCard>
                </Flexbox>
                <Text palette="neutral" shade="950" weight="500" size="textSm">
                    <Trans
                        i18nKey="projects:patient_absence_count"
                        values={{
                            total: totalPlannedAccessesInAbsence,
                            percentage: totalPlannedAccessesInAbsencePercentage
                        }}
                    />
                </Text>
                <Flexbox direction="column" gap={1} fullWidth>
                    <Grid columns={3} fullWidth>
                        <GridItem spanColumn={2}>
                            <Text palette="neutral" shade="950" weight="500">
                                {t('projects:absences_accesses')}
                            </Text>
                        </GridItem>
                        <GridItem>
                            <Text palette="neutral" shade="950" weight="500" size="textSm">
                                {totalPlannedAccessesInAbsence}
                            </Text>
                        </GridItem>
                    </Grid>
                    <DetailsFieldsCard size="sm" fullWidth gap={2} direction="column">
                        {plannedAccesses.non_in_person_accesses.map((access, index) => (
                            <Grid columns={3} gap={1} fullWidth key={index}>
                                <GridItem spanColumn={2}>
                                    <Text palette="neutral" shade="950" weight="500" size="textSm">
                                        {access.accessName}
                                    </Text>
                                </GridItem>
                                <GridItem>
                                    <Text palette="neutral" shade="950" weight="500" size="textSm">
                                        {access.numberOfAccesses}
                                    </Text>
                                </GridItem>
                            </Grid>
                        ))}
                    </DetailsFieldsCard>
                </Flexbox>
                {totalUnaccountedPlannedAccesses > 0 && plannedAccesses.unaccounted_accesses && (
                    <>
                        <Divider direction="horizontal" />
                        <Flexbox direction="column" gap={1} fullWidth>
                            <Grid columns={3} fullWidth>
                                <GridItem spanColumn={2}>
                                    <Text palette="neutral" shade="950" weight="500">
                                        {t('projects:unaccounted_accesses')}
                                    </Text>
                                </GridItem>
                                <GridItem>
                                    <Text palette="neutral" shade="950" weight="500" size="textSm">
                                        {totalUnaccountedPlannedAccesses}
                                    </Text>
                                </GridItem>
                            </Grid>
                            <DetailsFieldsCard size="sm" fullWidth gap={2} direction="column">
                                {plannedAccesses.unaccounted_accesses.map((access, index) => (
                                    <Grid columns={3} gap={1} fullWidth key={index}>
                                        <GridItem spanColumn={2}>
                                            <Text palette="neutral" shade="950" weight="500" size="textSm">
                                                {access.accessName}
                                            </Text>
                                        </GridItem>
                                        <GridItem>
                                            <Text palette="neutral" shade="950" weight="500" size="textSm">
                                                {access.numberOfAccesses}
                                            </Text>
                                        </GridItem>
                                    </Grid>
                                ))}
                            </DetailsFieldsCard>
                        </Flexbox>
                    </>
                )}
            </Flexbox>
        </DetailsFieldsSection>
    )
}
